import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';

interface ReportData {
  month: number;
  year: number;
  monthName: string;
  groupName: string;
  matchesPlayed: number;
  report: {
    topScorer: { name: string; avatar?: string; goals: number } | null;
    mvp: { name: string; avatar?: string; votes: number } | null;
    mostRegular: { name: string; avatar?: string; matches: number } | null;
    matchOfMonth: {
      date: string;
      location: string;
      team1Goals: number;
      team2Goals: number;
      team1Name: string;
      team2Name: string;
    } | null;
  } | null;
  records?: {
    allTimeTopScorer: { name: string; goals: number } | null;
    allTimeMostMatches: { name: string; matches: number } | null;
    bestMatch: { date: string; totalGoals: number; team1Goals: number; team2Goals: number } | null;
    bestStreak: { name: string; streak: number } | null;
  };
}

interface Props {
  groupId: string;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const AwardRow: React.FC<{
  icon: string;
  title: string;
  name: string;
  stat: string;
  avatar?: string | null;
}> = ({ icon, title, name, stat, avatar }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{ fontSize: 22 }}>{icon}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 11,
          color: theme.colors.textSecondary,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        {avatar && (
          <img
            src={avatar}
            alt=""
            style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }}
          />
        )}
        <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>{name}</span>
      </div>
    </div>
    <span style={{ fontSize: 15, fontWeight: 800, color: '#F59E0B' }}>{stat}</span>
  </div>
);

const RecordRow: React.FC<{ icon: string; text: string }> = ({ icon, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 16 }}>{icon}</span>
    <span style={{ fontSize: 13, color: theme.colors.text, flex: 1 }}>{text}</span>
  </div>
);

export const MonthlyReportCard: React.FC<Props> = ({ groupId }) => {
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(getApiUrl(`/api/groups/${groupId}/monthly-report?month=${viewMonth}&year=${viewYear}`), {
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setData(json);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, viewMonth, viewYear]);

  const goToPrev = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNext = () => {
    if (isCurrent) return;
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isCurrent = viewMonth === now.getMonth() + 1 && viewYear === now.getFullYear();

  const handleShare = async () => {
    if (!data?.report) return;
    const r = data.report;
    const lines = [`Resumen ${data.monthName} - ${data.groupName}`, ''];
    if (r.topScorer) lines.push(`⚽ Goleador: ${r.topScorer.name} (${r.topScorer.goals} goles)`);
    if (r.mvp) lines.push(`👑 MVP: ${r.mvp.name} (${r.mvp.votes} votos)`);
    if (r.mostRegular)
      lines.push(
        `💪 El más fiel: ${r.mostRegular.name} (${r.mostRegular.matches}/${data.matchesPlayed} partidos)`
      );
    lines.push(`\n${data.matchesPlayed} partidos jugados`);
    lines.push('\nSeguilo en Futbolero');
    const message = lines.join('\n');

    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({ text: message });
        return;
      } catch {
        // user cancelled or unsupported
      }
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(message);
        return;
      } catch {
        // ignore
      }
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Month navigator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
        }}
      >
        <button
          type="button"
          onClick={goToPrev}
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.textSecondary,
            cursor: 'pointer',
            fontSize: 22,
            padding: 4,
          }}
          aria-label="Mes anterior"
        >
          ‹
        </button>
        <span
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: theme.colors.text,
            textTransform: 'capitalize',
          }}
        >
          {data?.monthName || ''} {viewYear}
        </span>
        <button
          type="button"
          onClick={goToNext}
          disabled={isCurrent}
          style={{
            background: 'none',
            border: 'none',
            color: isCurrent ? theme.colors.border : theme.colors.textSecondary,
            cursor: isCurrent ? 'not-allowed' : 'pointer',
            fontSize: 22,
            padding: 4,
          }}
          aria-label="Mes siguiente"
        >
          ›
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: theme.colors.textSecondary }}>
          Cargando...
        </div>
      ) : !data?.report || data.matchesPlayed === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: theme.colors.textSecondary }}>
          Sin partidos en {data?.monthName || 'este mes'}
        </div>
      ) : (
        <>
          {/* Awards card */}
          <div
            style={{
              margin: '0 16px',
              background: theme.colors.surface,
              borderRadius: 14,
              border: `1px solid ${theme.colors.border}`,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {data.report.topScorer && (
              <AwardRow
                icon="⚽"
                title="Goleador del mes"
                name={data.report.topScorer.name}
                stat={`${data.report.topScorer.goals} goles`}
                avatar={data.report.topScorer.avatar}
              />
            )}
            {data.report.mvp && (
              <AwardRow
                icon="👑"
                title="MVP del mes"
                name={data.report.mvp.name}
                stat={`${data.report.mvp.votes} votos`}
                avatar={data.report.mvp.avatar}
              />
            )}
            {data.report.mostRegular && (
              <AwardRow
                icon="💪"
                title="El más fiel"
                name={data.report.mostRegular.name}
                stat={`${data.report.mostRegular.matches}/${data.matchesPlayed}`}
                avatar={data.report.mostRegular.avatar}
              />
            )}
            {data.report.matchOfMonth && (
              <AwardRow
                icon="🏟️"
                title="Partido del mes"
                name={data.report.matchOfMonth.location}
                stat={`${data.report.matchOfMonth.team1Goals}-${data.report.matchOfMonth.team2Goals}`}
              />
            )}
            <div
              style={{
                fontSize: 12,
                color: theme.colors.textSecondary,
                textAlign: 'center',
                marginTop: 4,
              }}
            >
              {data.matchesPlayed} partido{data.matchesPlayed !== 1 ? 's' : ''} jugado
              {data.matchesPlayed !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Share button */}
          <button
            type="button"
            onClick={handleShare}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              margin: '10px 16px 0',
              padding: '10px',
              borderRadius: 10,
              border: 'none',
              background: theme.colors.primary,
              color: theme.colors.background,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              width: 'calc(100% - 32px)',
            }}
          >
            📤 Compartir resumen
          </button>

          {/* Historical records */}
          {data.records && (
            <div
              style={{
                margin: '16px 16px 0',
                background: theme.colors.surface,
                borderRadius: 14,
                border: `1px solid ${theme.colors.border}`,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#F59E0B',
                  marginBottom: 4,
                }}
              >
                Records del grupo
              </div>
              {data.records.allTimeTopScorer && (
                <RecordRow
                  icon="⚽"
                  text={`Goleador historico: ${data.records.allTimeTopScorer.name} (${data.records.allTimeTopScorer.goals} goles)`}
                />
              )}
              {data.records.bestStreak && data.records.bestStreak.streak > 0 && (
                <RecordRow
                  icon="🔥"
                  text={`Mejor racha: ${data.records.bestStreak.name} (${data.records.bestStreak.streak} partidos seguidos)`}
                />
              )}
              {data.records.allTimeMostMatches && (
                <RecordRow
                  icon="🏆"
                  text={`Mas partidos: ${data.records.allTimeMostMatches.name} (${data.records.allTimeMostMatches.matches})`}
                />
              )}
              {data.records.bestMatch && (
                <RecordRow
                  icon="🏟️"
                  text={`Mayor goleada: ${data.records.bestMatch.team1Goals}-${data.records.bestMatch.team2Goals} (${data.records.bestMatch.totalGoals} goles)`}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyReportCard;
